// GA portal client view (GET /api/ga/:gaId/clients/:clientId). Unlike other GA
// fetches, non-OK responses are kept as typed errors so the page can tell
// "no access" from "not shared" from a plain failure.

export interface GaClientPlan {
  id: number;
  planName: string;
  planType: string;
  carrierName: string | null;
  effectiveDate: string | null;
  endDate: string | null;
  renewalDate: string | null;
}

export interface GaClientView {
  client: {
    id: number;
    name: string;
    status: string;
    city: string | null;
    state: string | null;
    primaryContactName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    groupNumber: string | null;
    clientSince: string | null;
    renewalStage: string | null;
  };
  broker: { brokerAccountId: string; brokerAgencyName: string | null };
  scope: "view_only" | "assist";
  sharedAt: string | null;
  employeeCount: number;
  enrolledCount: number;
  waivedCount: number;
  enrollmentPct: number | null;
  planYear: { effectiveDate: string | null; renewalDate: string | null };
  plans: GaClientPlan[];
}

export class GaClientViewError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "GaClientViewError";
  }
}

export function gaClientViewUrl(gaId: string, clientId: string | number, brokerAccountId?: string) {
  const base = `/api/ga/${encodeURIComponent(gaId)}/clients/${encodeURIComponent(String(clientId))}`;
  return brokerAccountId ? `${base}?brokerAccountId=${encodeURIComponent(brokerAccountId)}` : base;
}

export async function fetchGaClientView(url: string, fetchImpl: typeof fetch = fetch): Promise<GaClientView> {
  const res = await fetchImpl(url, { credentials: "include" });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = (await res.json())?.message || message;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new GaClientViewError(res.status, message);
  }
  return res.json();
}

export type GaClientPageState = "loading" | "ready" | "unauthenticated" | "forbidden" | "not_shared" | "error";

export function gaClientPageState(q: { isLoading: boolean; error: unknown; data: unknown }): GaClientPageState {
  if (q.isLoading) return "loading";
  if (q.error) {
    const status = q.error instanceof GaClientViewError ? q.error.status : 0;
    if (status === 401) return "unauthenticated";
    if (status === 403) return "forbidden";
    if (status === 404) return "not_shared";
    return "error";
  }
  return q.data ? "ready" : "error";
}
